import { Flex, Text } from '@chakra-ui/react';
import { FormFeedback, OutlineCard, TextArea } from '@morressier/alembic';
import { get } from 'lodash';
import { FieldError, UseFormReturn } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { RadioGroup } from 'src/components/RadioGroup';
import { FormFieldDependentText } from '.';
import { IDependentTextValue } from './DependentTextUpdateModal';
import { getValidationTypedMessage } from 'src/components/FormField/common/errorMessages';
import { toSentenceCasePreserveAcronyms } from 'src/utils/string';

interface Props {
  config: FormFieldDependentText;
  methods: UseFormReturn<IDependentTextValue>;
  formContext?: 'create' | 'edit';
}

export const DependentTextForm: React.FC<Props> = ({
  config,
  methods,
  formContext = 'edit'
}) => {
  const {
    control,
    formState: { errors },
    watch,
    register,
    setValue
  } = methods;
  const {
    params: { validations, labels },
    caseDataFields: { main: field, dependsOn }
  } = config;

  const dependsOnField = `${field}.${dependsOn}` as const;
  const mainField = `${field}.value` as const;

  const value = watch(mainField);
  const hasValue = watch(dependsOnField);
  const mainError = get(errors, mainField);
  const dependsOnError = get(errors, dependsOnField);
  return (
    <Flex flexDirection="column" gap={4} flex={1}>
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingSection">
          Update {config.params.labels.main}
        </Text>
      </Flex>

      <OutlineCard
        background={
          formContext === 'edit' ? 'surfaceSecondaryFill' : 'surfacePrimaryFill'
        }
        borderless
        padding={8}
      >
        <Flex flexDirection="column" gap={4}>
          <FieldLabel
            isRequired={validations.required}
            label={`Does submission have ${labels.main}?`}
            subText=""
            helpText={labels.helpText}
          />

          <RadioGroup
            name={dependsOnField}
            control={control}
            options={[
              {
                value: true,
                children: labels.options['true']?.main
              },
              {
                value: false,
                children: labels.options['false']?.main
              }
            ]}
            onChange={() => {
              setValue(mainField, '');
            }}
          />

          {dependsOnError ? (
            <FormFeedback variant="error">
              Please indicate whether your submission {labels.main} to proceed.
            </FormFeedback>
          ) : null}
        </Flex>
      </OutlineCard>

      {hasValue === true &&
      (labels.section.title.length > 0 ||
        labels.section.explanation.length > 0) ? (
        <Flex flexDirection="column" gap={4}>
          <FieldLabel
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
          />
          <Flex flex={1} width={'100%'} gap={4}>
            <TextArea
              placeholder={`${toSentenceCasePreserveAcronyms(
                `Enter ${
                  labels.main === 'Previously Submitted'
                    ? 'Manuscript ID'
                    : labels.main === 'Preprint'
                    ? 'Preprint Link'
                    : labels.main
                }`
              )}`}
              value={value ?? ''}
              {...register(mainField)}
              isInvalid={Boolean(mainError)}
              errorMessage={
                Boolean(mainError) &&
                getValidationTypedMessage(
                  labels.main === 'Previously Submitted'
                    ? 'Manuscript ID'
                    : labels.main === 'Preprint'
                    ? 'Preprint Link'
                    : labels.main,
                  validations.kind,
                  validations,
                  mainError as FieldError
                )
              }
              minHeight="100px"
            />
          </Flex>
        </Flex>
      ) : hasValue === false && config.params?.labels?.sectionNo ? (
        <Flex flexDirection="column" gap={4}>
          <FieldLabel
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
          />
          <Flex flex={1} width={'100%'} gap={4}>
            <TextArea
              placeholder={`${toSentenceCasePreserveAcronyms(
                `Enter ${labels.main}`
              )}`}
              value={value ?? ''}
              {...register(mainField)}
              isInvalid={Boolean(mainError)}
              errorMessage={
                Boolean(mainError) &&
                getValidationTypedMessage(
                  labels.main === 'Previously Submitted'
                    ? 'Manuscript ID'
                    : labels.main === 'Preprint'
                    ? 'Preprint Link'
                    : labels.main,
                  validations.kind,
                  validations,
                  mainError as FieldError
                )
              }
              minHeight="100px"
            />
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  );
};
